import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Clock, Facebook, Instagram, Send, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/context/LanguageContext";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const recaptchaRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!formData.subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    if (!formData.message.trim() || formData.message.length < 10) {
      toast.error("Please enter a message (at least 10 characters)");
      return;
    }
    
    // Get reCAPTCHA token
    const captchaToken = recaptchaRef.current?.getValue();
    if (!captchaToken) {
      toast.error("Please complete the reCAPTCHA verification");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          captcha_token: captchaToken
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || "Message sent successfully!");
        setFormData({ name: "", email: "", subject: "", message: "" });
        recaptchaRef.current?.reset();
      } else {
        toast.error(data.detail || "Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Network error. Please check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-16" data-testid="contact-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('backToHome')}
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <p className="label-style mb-2">{t('getInTouch')}</p>
          <h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            {t('contactUs')}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Have a question about our products or need help with your order? We're here to help!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-white border border-border rounded-lg p-6 md:p-8">
            <h2 
              className="font-bold text-xl mb-6"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              {t('sendUsMessage')}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="space-y-2">
                <Label htmlFor="name">{t('name')} *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder={t('name')}
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={submitting}
                  data-testid="contact-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')} *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={submitting}
                  data-testid="contact-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subject">{t('subject')} *</Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder={t('subject')}
                  value={formData.subject}
                  onChange={handleInputChange}
                  disabled={submitting}
                  data-testid="contact-subject-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">{t('message')} *</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder={t('message')}
                  rows={5}
                  value={formData.message}
                  onChange={handleInputChange}
                  disabled={submitting}
                  data-testid="contact-message-input"
                />
              </div>
              
              {/* reCAPTCHA */}
              <div className="flex justify-center py-2" data-testid="recaptcha-container">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full rounded-full"
                disabled={submitting}
                data-testid="contact-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('sendMessage')}
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* Email Section */}
            <div className="bg-white border border-border rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 
                    className="font-bold text-lg mb-1"
                    style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
                  >
                    {t('emailUs')}
                  </h3>
                  <a 
                    href="mailto:ministripesuk@gmail.com" 
                    className="text-primary hover:underline text-lg"
                    data-testid="contact-email"
                  >
                    ministripesuk@gmail.com
                  </a>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('weTypicallyRespond')}
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp Section */}
            <div className="bg-white border border-border rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                  <MessageCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 
                    className="font-bold text-lg mb-1"
                    style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
                  >
                    {t('whatsApp')}
                  </h3>
                  <a 
                    href="https://wa.me/447852885975" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:underline text-lg"
                    data-testid="contact-whatsapp"
                  >
                    +44 7852 885975
                  </a>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('textMessagesOnly')}
                  </p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-white border border-border rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center shrink-0">
                  <Clock className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 
                    className="font-bold text-lg mb-1"
                    style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
                  >
                    {t('businessHours')}
                  </h3>
                  <p className="text-muted-foreground">
                    Monday - Friday: 9am - 5pm<br />
                    Saturday: Closed<br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white border border-border rounded-lg p-6">
              <h3 
                className="font-bold text-lg mb-4"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                {t('followUs')}
              </h3>
              <div className="flex gap-4">
                <a 
                  href="https://www.facebook.com/profile.php?id=61572637403452" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                  data-testid="contact-facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.instagram.com/MinistripesUK" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                  data-testid="contact-instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Teaser */}
        <div className="text-center bg-secondary/30 p-8 rounded-lg mt-8">
          <h3 
            className="font-bold text-xl mb-2"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            {t('commonQuestions')}
          </h3>
          <p className="text-muted-foreground mb-4">
            Looking for shipping info, returns policy, or product details?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/shipping-returns" className="text-primary hover:underline">{t('shipping')}</a>
            <span className="text-muted-foreground">•</span>
            <a href="/shipping-returns" className="text-primary hover:underline">{t('returnsPolicy')}</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
